import React, { useState, useEffect } from "react";
import BoldIcon from "../assets/icons/BoldIcon.svg";
import CircleLineIcon from "../assets/icons/circleLineIcon.svg";
import NumberIconLine from "../assets/icons/NumberIcon.svg";
import FlexIcon from "../assets/icons/flexIcon.svg";
import RotateTextIcon from "../assets/icons/rotateTextIcon.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { jobsEndpoint } from '../constants';

function JobDescription() {
  const navigate = useNavigate();
  const location = useLocation();

  // Form state
  const [formData, setFormData] = useState({
    body: '',
    requirements: '',
    category: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jobId, setJobId] = useState(null);

// Handle file uploads
const handleFileChange = (e) => {
  const files = Array.from(e.target.files);
  setFormData(prev => ({
    ...prev,
    content: files
  }));
  
  // Clear any existing file errors
  if (errors.content) {
    setErrors(prev => ({
      ...prev,
      content: ''
    }));
  }
};

  // Get job ID from previous step
  useEffect(() => {
    if (location.state?.jobId) {
      setJobId(location.state.jobId);
    } else {
      console.log(jobId)
    }
  }, [location, navigate]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.body.trim()) {
      newErrors.body = 'Job description is required';
    }
    
    if (!formData.requirements.trim()) {
      newErrors.requirements = 'Requirements are required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!jobId) {
      setErrors({ submit: 'Job data not found. Please start from the beginning.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${jobsEndpoint}update/step2/${jobId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        navigate("/salary_job", { state: { jobId } });
      } else {
        const errorData = await response.json();
        setErrors(errorData);
      }
    } catch (error) {
      console.error('Error updating job description:', error);
      setErrors({ submit: 'An error occurred while updating the job description' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom textarea component with character count
  const CustomTextarea = ({ value, onChange, placeholder, error, label }) => (
    <div className="w-full flex flex-col gap-2">
      <label className="text-[#414651] text-sm font-medium">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full h-38 p-3 rounded-lg border text-black bg-white outline-none resize-none ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      />
      <div className="flex justify-between">
        <p className="text-gray-400 text-sm">
          <span className="text-black">{value.length}</span> characters
        </p>
        {error && <span className="text-red-500 text-sm">{error}</span>}
      </div>
    </div>
  );

  return (
    <div className="w-full pb-[8.625rem] lg:pr-[1.875rem] p-0">
      <div className="main_wrap flex flex-col items-start pt-[var(--spacing-md, 8px)]">
        <div className="main flex flex-col items-start gap-8 self-stretch pt-8 pb-12">
          <div className="flex flex-col items-start gap-6 self-stretch">
            <div className="container flex flex-col items-start self-stretch pb-[32px] pl-[var(--container-padding-desktop, 32px)] pr-[var(--container-padding-desktop, 32px)] pt-0 px-8">
              <div className="page_header flex flex-col items-start">
                <div className="flex flex-wrap items-start content-start">
                  <div className="text_and_supporting_text-2 flex flex-col items-start min-w-[20rem]">
                    <div className="self-stretch text-[#181d27] font-semibold">
                      Create Job
                    </div>
                    <div className="self-stretch text-[#535862] font-['var(--Font-family-font-family-body, Inter)'] text-[var(--Font-size-text-md, 16px)] leading-[var(--Line-height-text-md, 24px)]">
                      Here is a complete overview of your jobs.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex flex-col items-center justify-center w-full">
            <div className="flex justify-center items-start w-full">
              <div className="_step_base flex flex-col items-center w-60">
                <div className="flex flex-col items-center self-stretch">
                  <div className="self-stretch text-[#009c97] lg:text-base text-sm text-center font-semibold">
                    Define your role
                  </div>
                  <div className="self-stretch text-[#009c97] text-center lg:text-base text-sm">
                  </div>
                </div>
                <div className="_step_icon_base flex justify-center items-center w-6 h-6 rounded-full bg-[#f9f5ff]">
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="#19BFB7" />
                    <circle cx={12} cy={12} r={4} fill="white" />
                  </svg>
                </div>
              </div>
              
              <div className="_step_base-1 flex flex-col items-center w-60">
                <div className="flex flex-col items-center self-stretch">
                  <div className="self-stretch text-[#009c97] text-center font-semibold lg:text-base text-sm">
                    Job Description
                  </div>
                  <div className="self-stretch text-[#009c97] text-center lg:text-base text-sm">
                  </div>
                </div>
                <div className="flex justify-center items-center w-6 h-6 rounded-full bg-[#f9f5ff]">
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12Z" fill="#19BFB7" />
                    <circle cx={12} cy={12} r={4} fill="white" />
                  </svg>
                </div>
              </div>
              
              <div className="_step_base-2 flex flex-col items-center w-60">
                <div className="flex flex-col items-center self-stretch">
                  <div className="self-stretch text-[#414651] text-center font-semibold lg:text-base text-sm">
                    Salary & Benefits
                  </div>
                  <div className="self-stretch text-[#535862] text-center lg:text-base text-sm">
                  </div>
                </div>
                <div className="flex justify-center items-center w-6 h-6 rounded-full bg-neutral-50">
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.75 12C0.75 5.7868 5.7868 0.75 12 0.75C18.2132 0.75 23.25 5.7868 23.25 12C23.25 18.2132 18.2132 23.25 12 23.25C5.7868 23.25 0.75 18.2132 0.75 12Z" stroke="#E9EAEB" strokeWidth="1.5" />
                    <circle cx={12} cy={12} r={4} fill="#D5D7DA" />
                  </svg>
                </div>
              </div>
              
              <div className="_step_base-3 flex flex-col items-center w-60">
                <div className="flex flex-col items-center self-stretch">
                  <div className="self-stretch text-[#414651] text-center font-semibold lg:text-base text-sm">
                    Hiring Preferences
                  </div>
                  <div className="self-stretch text-[#535862] text-center lg:text-base text-sm">
                  </div>
                </div>
                <div className="flex justify-center items-center w-6 h-6 rounded-full bg-neutral-50">
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0.75 12C0.75 5.7868 5.7868 0.75 12 0.75C18.2132 0.75 23.25 5.7868 23.25 12C23.25 18.2132 18.2132 23.25 12 23.25C5.7868 23.25 0.75 18.2132 0.75 12Z" stroke="#E9EAEB" strokeWidth="1.5" />
                    <circle cx={12} cy={12} r={4} fill="#D5D7DA" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex items-start gap-6 w-full">
              <div className="flex w-full flex-col items-start gap-6 self-stretch pb-[32px] pt-0 px-8">
                <div className="flex md:flex-row flex-col items-start gap-6 self-stretch">
                  <div className="flex flex-col items-start gap-1 lg:w-[17.5rem] lg:min-w-[12.5rem] lg:max-w-[17.5rem]">
                    <div className="text_wrapper flex items-center">
                      <div className="text-[#414651] font-semibold leading-6">
                        Job Details
                      </div>
                      <div className="text-[#009c97] text-sm font-semibold ml-1">*</div>
                    </div>
                    <div className="text-[#535862] text-sm">
                      Provide detailed information about the position.
                    </div>
                  </div>
  
                  {/* Form Fields */}
                  <div className="w-full flex flex-col gap-8">
                    {/* Job Description */}
                    <div className="flex flex-col gap-2">
                      <label className="text-[#414651] text-sm font-medium">
                        Job Description *
                      </label>
                      <textarea
                        value={formData.body}
                        onChange={(e) => handleInputChange('body', e.target.value)}
                        placeholder="Enter full job description..."
                        className={`w-full h-48 p-3 rounded-lg border text-black bg-white outline-none resize-none ${
                          errors.body ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.body && <span className="text-red-500 text-sm">{errors.body}</span>}
                      <div className="text-gray-600 text-sm">
                        {formData.body.length} characters
                      </div>
                    </div>
  
                    {/* Requirements */}
                    <div className="flex flex-col gap-2">
                      <label className="text-[#414651] text-sm font-medium">
                        Requirements *
                      </label>
                      <textarea
                        value={formData.requirements}
                        onChange={(e) => handleInputChange('requirements', e.target.value)}
                        placeholder="List key requirements..."
                        className={`w-full h-32 p-3 rounded-lg border text-black bg-white outline-none resize-none ${
                          errors.requirements ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.requirements && <span className="text-red-500 text-sm">{errors.requirements}</span>}
                      <div className="text-gray-600 text-sm">
                        {formData.requirements.length} characters
                      </div>
                    </div>
  

  
        
  
                    {/* Submission Error */}
                    {errors.submit && (
                      <div className="text-red-500 text-sm mt-4">{errors.submit}</div>
                    )}
                  </div>
                </div>
  
                {/* Action Buttons */}
                <div className="flex justify-end gap-4 mt-8 w-full">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 py-2 rounded-lg text-white ${
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-[#19BFB7] hover:bg-[#16a8a1] transition-colors'
                    }`}
                  >
                    {isSubmitting ? 'Saving...' : 'Save & Continue'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
  );
}

export default JobDescription;